import { useCallback, useEffect } from 'react'
import groupBy from 'lodash/groupBy'
import isEqual from 'lodash/isEqual'
import { P, Wrapper } from '@farewill/ui'
import { useLocation } from 'react-router-dom'

import usePrevious from 'lib/effects/use-previous'
import { Task } from 'lib/models/task'
import { PRODUCTS } from 'utils/enums'

import { useTasksContext } from './context'
import TasksStage from './tasks-stage'
import { CORE_TASKS_IN_ORDER } from './constants'

import { getSortedTasks } from './tasks-sorter'

const taskIsComplete = (task: Task) => task.attributes.completedAt

const getStageIsExpanded = ({
  prevTasks,
  currentTasks,
}: {
  prevTasks: Task[]
  currentTasks: Task[]
}) => {
  const prevStageComplete = prevTasks?.every(taskIsComplete)
  const currentStageComplete = currentTasks?.every(taskIsComplete)

  return (!prevTasks || prevStageComplete) && !currentStageComplete
}

type TaskListProps = {
  queryParams: Record<string, string | number | boolean>
  product: keyof typeof CORE_TASKS_IN_ORDER
  isPanelExpanded: boolean
  setPanelExpanded: (isPanelExpanded: boolean) => void
  productId: object
  enableHelpAssignment?: boolean
}

const TasksList = ({
  queryParams,
  product,
  isPanelExpanded,
  setPanelExpanded,
  productId,
  enableHelpAssignment = false,
}: TaskListProps) => {
  const { tasks, fetchTasks, isFetchingTasks } = useTasksContext()
  const stages = CORE_TASKS_IN_ORDER[product]
  const prevQueryParams = usePrevious(queryParams)
  const location = useLocation()
  const refetchTasks = useCallback(
    () => fetchTasks({ queryParams }),
    [fetchTasks, queryParams]
  )

  useEffect(() => {
    if (!isEqual(queryParams, prevQueryParams)) {
      fetchTasks({ queryParams })
    }
  }, [fetchTasks, queryParams, prevQueryParams])

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const scrollId = location.hash.replace('#', '')
        const task = document.querySelector(`[data-scroll-id="${scrollId}"]`)
        if (task) task.scrollIntoView({ behavior: 'smooth' })
      }, 300)
    }
  }, [location.hash, isFetchingTasks])

  const sortedTasks = getSortedTasks(CORE_TASKS_IN_ORDER, tasks, product)
  const groupedTasks = groupBy(sortedTasks, 'attributes.stage')
  const stagesWithTasks = stages.filter(({ name }) => groupedTasks[name])
  const enableCommenting =
    product === PRODUCTS.PROBATE || product === PRODUCTS.WILL

  if (tasks.length === 0 && isFetchingTasks) {
    return <P>Loading...</P>
  }

  return (
    <Wrapper>
      {stagesWithTasks.map((stage, index) => (
        <TasksStage
          productId={productId}
          tasks={groupedTasks[stage.name]}
          title={stage.title}
          enableCommenting={enableCommenting}
          enableHelpAssignment={enableHelpAssignment}
          key={stage.name}
          stage={stage.name}
          isPanelExpanded={isPanelExpanded}
          setPanelExpanded={setPanelExpanded}
          isStageExpanded={
            isPanelExpanded ||
            getStageIsExpanded({
              prevTasks: groupedTasks[stagesWithTasks[index - 1]?.name],
              currentTasks: groupedTasks[stage.name],
            })
          }
          refetchTasks={refetchTasks}
        />
      ))}
    </Wrapper>
  )
}

TasksList.displayName = 'TasksList'

export default TasksList
